// src/FAQManagement.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import axios from 'axios';
import {
  Container,
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Alert,
  CircularProgress,
  IconButton,
  Collapse,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Card,
  CardContent,
  CardActions,
  InputAdornment,
  AppBar,
  Toolbar,
  Tooltip,
  Tabs,
  Tab,
  Badge,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import UserMenu from './components/UserMenu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const TOPIC_OPTIONS = [
  'General',
  'Account Management',
  'Billing',
  'Technical Support',
  'Product Features',
  'Integrations',
  'API',
  'Security',
  'Troubleshooting',
  'Other'
];

function FAQManagement({ handleLogout }) {
  const navigate = useNavigate();
  
  // State
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTopic, setFilterTopic] = useState('all');
  const [sortBy, setSortBy] = useState('usage'); // 'usage', 'date', 'quality'
  
  // Create new FAQ state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newQuestion, setNewQuestion] = useState('');
  const [newAnswer, setNewAnswer] = useState('');
  const [newTopic, setNewTopic] = useState('General');
  const [newScope, setNewScope] = useState('company'); // 'company' or 'team'
  const [newTeamId, setNewTeamId] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  
  // Edit FAQ state
  const [editingFaqId, setEditingFaqId] = useState(null);
  const [editQuestion, setEditQuestion] = useState('');
  const [editAnswer, setEditAnswer] = useState('');
  const [editTopic, setEditTopic] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [faqToDelete, setFaqToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // User data
  const [userId, setUserId] = useState(null);
  const [userCompany, setUserCompany] = useState(null);
  const [userTeams, setUserTeams] = useState([]); // NEW: User's teams
  const [selectedTeam, setSelectedTeam] = useState('all'); // NEW: Filter by team
  
  // Pending FAQs state
  const [activeTab, setActiveTab] = useState('published'); // 'published' or 'pending'
  const [pendingFaqs, setPendingFaqs] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);
  const [reviewingSuggestionId, setReviewingSuggestionId] = useState(null);
  const [reviewQuestion, setReviewQuestion] = useState('');
  const [reviewAnswer, setReviewAnswer] = useState('');
  const [reviewTopic, setReviewTopic] = useState('General');
  const [isApproving, setIsApproving] = useState(false);

  // Get auth token
  const getAuthToken = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token;
  }, []);

  // Fetch user and company
  useEffect(() => {
    async function fetchUserData() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          setUserId(user.id);
          
          // Get user's company
          const token = await getAuthToken();
          const response = await axios.get(`${BACKEND_URL}/api/v1/user/company`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data) {
            setUserCompany(response.data);
          }
          
          // Get user's teams
          const teamsResponse = await axios.get(`${BACKEND_URL}/api/v1/user/teams`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (teamsResponse.data) {
            setUserTeams(teamsResponse.data);
          }
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
      }
    }
    fetchUserData();
  }, [getAuthToken]);

  // Fetch FAQs
  const fetchFAQs = useCallback(async () => {
    if (!userCompany?.id) return;
    
    setLoading(true);
    setError('');
    
    try {
      const token = await getAuthToken();
      const params = { company_id: userCompany.id };
      
      // Add team filter if selected
      if (selectedTeam && selectedTeam !== 'all') {
        params.team_id = selectedTeam;
      }
      
      const response = await axios.get(`${BACKEND_URL}/api/faqs`, {
        headers: { Authorization: `Bearer ${token}` },
        params: params
      });
      
      setFaqs(response.data || []);
    } catch (err) {
      console.error('Error fetching FAQs:', err);
      setError('Failed to load FAQs. ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }, [userCompany, selectedTeam, getAuthToken]);

  useEffect(() => {
    if (userCompany?.id) {
      fetchFAQs();
    }
  }, [userCompany, fetchFAQs]);

  // Fetch Pending FAQs
  const fetchPendingFAQs = useCallback(async () => {
    if (!userCompany?.id) return;
    
    setLoadingPending(true);
    setError('');
    
    try {
      const token = await getAuthToken();
      const params = { company_id: userCompany.id };
      
      // Add team filter if selected
      if (selectedTeam && selectedTeam !== 'all') {
        params.team_id = selectedTeam;
      }
      
      const response = await axios.get(`${BACKEND_URL}/api/faqs/pending`, {
        headers: { Authorization: `Bearer ${token}` },
        params: params
      });
      
      setPendingFaqs(response.data || []);
    } catch (err) {
      console.error('Error fetching pending FAQs:', err);
      setError('Failed to load pending FAQs. ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoadingPending(false);
    }
  }, [userCompany, selectedTeam, getAuthToken]);

  useEffect(() => {
    if (userCompany?.id && activeTab === 'pending') {
      fetchPendingFAQs();
    }
  }, [userCompany, activeTab, fetchPendingFAQs]);

  // Handle review pending FAQ
  const handleStartReview = (suggestion) => {
    setReviewingSuggestionId(suggestion.id);
    setReviewQuestion(suggestion.question);
    setReviewAnswer(suggestion.edited_answer || suggestion.original_answer);
    setReviewTopic('General');
  };

  // Handle approve pending FAQ
  const handleApprovePending = async () => {
    if (!reviewQuestion.trim() || !reviewAnswer.trim()) {
      setError('Question and answer are required');
      return;
    }
    
    if (!window.confirm('Approve this FAQ and add it to your knowledge base?\n\nIt will be embedded and used in searches.')) {
      return;
    }
    
    setIsApproving(true);
    setError('');
    setMessage('');
    
    try {
      const token = await getAuthToken();
      await axios.post(
        `${BACKEND_URL}/api/faqs/pending/${reviewingSuggestionId}/approve`,
        {
          question: reviewQuestion.trim(),
          answer: reviewAnswer.trim(),
          topic: reviewTopic
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setMessage('FAQ approved and created successfully!');
      setReviewingSuggestionId(null);
      
      // Refresh pending list
      await fetchPendingFAQs();
      
    } catch (err) {
      console.error('Error approving FAQ:', err);
      setError('Failed to approve FAQ: ' + (err.response?.data?.detail || err.message));
    } finally {
      setIsApproving(false);
    }
  };

  // Handle reject pending FAQ
  const handleRejectPending = async (suggestionId) => {
    if (!window.confirm('Reject this FAQ suggestion?\n\nIt will not be added to your knowledge base.')) {
      return;
    }
    
    try {
      const token = await getAuthToken();
      await axios.post(
        `${BACKEND_URL}/api/faqs/pending/${suggestionId}/reject`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setMessage('FAQ suggestion rejected.');
      
      // Refresh pending list
      await fetchPendingFAQs();
      
    } catch (err) {
      console.error('Error rejecting FAQ:', err);
      setError('Failed to reject FAQ: ' + (err.response?.data?.detail || err.message));
    }
  };

  // Handle cancel review
  const handleCancelReview = () => {
    setReviewingSuggestionId(null);
    setReviewQuestion('');
    setReviewAnswer('');
    setReviewTopic('General');
  };

  // Handle create FAQ
  const handleCreateFAQ = async () => {
    if (!newQuestion.trim() || !newAnswer.trim()) {
      setError('Question and answer are required');
      return;
    }
    
    // Validate team selection for team-scoped FAQs
    if (newScope === 'team' && !newTeamId) {
      setError('Please select a team for team-scoped FAQ');
      return;
    }
    
    const scopeMsg = newScope === 'team' ? 'team-specific' : 'company-wide';
    if (!window.confirm(`Create this ${scopeMsg} FAQ entry?\n\nThis will be automatically embedded and available for searches.`)) {
      return;
    }
    
    setIsCreating(true);
    setError('');
    setMessage('');
    
    try {
      const token = await getAuthToken();
      const payload = {
        question: newQuestion.trim(),
        answer: newAnswer.trim(),
        topic: newTopic,
        company_id: userCompany.id,
        scope: newScope
      };
      
      // Add team_id for team-scoped FAQs
      if (newScope === 'team' && newTeamId) {
        payload.team_id = newTeamId;
      }
      
      const response = await axios.post(
        `${BACKEND_URL}/api/faqs`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setMessage(`FAQ created successfully (${scopeMsg})! It will be embedded shortly.`);
      setNewQuestion('');
      setNewAnswer('');
      setNewTopic('General');
      setNewScope('company');
      setNewTeamId('');
      setShowCreateForm(false);
      
      // Refresh list
      await fetchFAQs();
      
    } catch (err) {
      console.error('Error creating FAQ:', err);
      setError('Failed to create FAQ: ' + (err.response?.data?.detail || err.message));
    } finally {
      setIsCreating(false);
    }
  };

  // Handle start edit
  const handleStartEdit = (faq) => {
    setEditingFaqId(faq.id);
    setEditQuestion(faq.question);
    setEditAnswer(faq.answer);
    setEditTopic(faq.topic || 'General');
  };

  // Handle save edit
  const handleSaveEdit = async () => {
    if (!editQuestion.trim() || !editAnswer.trim()) {
      setError('Question and answer are required');
      return;
    }
    
    if (!window.confirm('Save changes to this FAQ?\n\nThe old version will be replaced and the new version will be re-embedded.')) {
      return;
    }
    
    setIsEditing(true);
    setError('');
    setMessage('');
    
    try {
      const token = await getAuthToken();
      await axios.put(
        `${BACKEND_URL}/api/faqs/${editingFaqId}`,
        {
          question: editQuestion.trim(),
          answer: editAnswer.trim(),
          topic: editTopic
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setMessage('FAQ updated successfully! It will be re-embedded shortly.');
      setEditingFaqId(null);
      
      // Refresh list
      await fetchFAQs();
      
    } catch (err) {
      console.error('Error updating FAQ:', err);
      setError('Failed to update FAQ: ' + (err.response?.data?.detail || err.message));
    } finally {
      setIsEditing(false);
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingFaqId(null);
    setEditQuestion('');
    setEditAnswer('');
    setEditTopic('');
  };

  // Handle delete
  const handleDeleteClick = (faq) => {
    setFaqToDelete(faq);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!faqToDelete) return;
    
    setIsDeleting(true);
    setError('');
    setMessage('');
    
    try {
      const token = await getAuthToken();
      await axios.delete(
        `${BACKEND_URL}/api/faqs/${faqToDelete.id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setMessage('FAQ deleted successfully.');
      setDeleteDialogOpen(false);
      setFaqToDelete(null);
      
      // Refresh list
      await fetchFAQs();
      
    } catch (err) {
      console.error('Error deleting FAQ:', err);
      setError('Failed to delete FAQ: ' + (err.response?.data?.detail || err.message));
    } finally {
      setIsDeleting(false);
    }
  };

  // Filter and sort FAQs
  const filteredAndSortedFAQs = React.useMemo(() => {
    let filtered = faqs;
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(faq => 
        faq.question.toLowerCase().includes(query) ||
        faq.answer.toLowerCase().includes(query)
      );
    }
    
    // Apply topic filter
    if (filterTopic !== 'all') {
      filtered = filtered.filter(faq => faq.topic === filterTopic);
    }
    
    // Apply sorting
    const sorted = [...filtered];
    switch (sortBy) {
      case 'usage':
        sorted.sort((a, b) => (b.usage_count || 0) - (a.usage_count || 0));
        break;
      case 'date':
        sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
      case 'quality':
        sorted.sort((a, b) => (b.quality_score || 0) - (a.quality_score || 0));
        break;
      default:
        break;
    }
    
    return sorted;
  }, [faqs, searchQuery, filterTopic, sortBy]);

  // Get unique topics from FAQs
  const availableTopics = React.useMemo(() => {
    const topics = new Set(faqs.map(faq => faq.topic).filter(Boolean));
    return Array.from(topics).sort();
  }, [faqs]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* AppBar */}
      <AppBar position="static" sx={{ background: '#fff', borderBottom: '1px solid #e0e0e0' }}>
        <Toolbar variant="dense">
          <Tooltip title="Back to Chat">
            <IconButton
              sx={{ mr: 1, color: '#000' }}
              onClick={() => navigate('/')}
              edge="start"
            >
              <MenuIcon />
            </IconButton>
          </Tooltip>
          
          <QuestionAnswerIcon sx={{ mr: 1, color: '#1976d2' }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: '#000' }}>
            FAQ Management
          </Typography>
          
          <UserMenu
            onLogout={handleLogout}
            onOpenSettings={() => {}}
            onOpenProfile={() => {}}
          />
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, flexGrow: 1, overflow: 'auto' }}>
        {/* Header Actions */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h4" component="h1">
            FAQ Library
          </Typography>
          
          <Button
            variant="contained"
            startIcon={showCreateForm ? <ExpandLessIcon /> : <AddIcon />}
            onClick={() => setShowCreateForm(!showCreateForm)}
            sx={{ textTransform: 'none' }}
          >
            {showCreateForm ? 'Hide Form' : 'Create New FAQ'}
          </Button>
        </Box>

        {/* Success/Error Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        
        {message && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setMessage('')}>
            {message}
          </Alert>
        )}

        {/* Tabs: Published FAQs vs Pending Approvals */}
        <Paper sx={{ mb: 3 }}>
          <Tabs 
            value={activeTab} 
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab 
              label="Published FAQs" 
              value="published"
              sx={{ textTransform: 'none', fontWeight: activeTab === 'published' ? 600 : 400 }}
            />
            <Tab 
              label={
                <Badge badgeContent={pendingFaqs.length} color="warning">
                  <span style={{ paddingRight: pendingFaqs.length > 0 ? '12px' : '0' }}>
                    Pending Approval
                  </span>
                </Badge>
              }
              value="pending"
              sx={{ textTransform: 'none', fontWeight: activeTab === 'pending' ? 600 : 400 }}
            />
          </Tabs>
        </Paper>

        {/* Create New FAQ Form */}
        <Collapse in={showCreateForm}>
          <Paper sx={{ p: 3, mb: 3, bgcolor: '#f5f5f5' }}>
            <Typography variant="h6" gutterBottom>
              Create New FAQ
            </Typography>
            
            <TextField
              label="Question"
              fullWidth
              multiline
              rows={2}
              value={newQuestion}
              onChange={(e) => setNewQuestion(e.target.value)}
              placeholder="Enter the question users might ask..."
              sx={{ mb: 2 }}
              helperText={`${newQuestion.length} characters`}
            />
            
            <TextField
              label="Answer"
              fullWidth
              multiline
              rows={4}
              value={newAnswer}
              onChange={(e) => setNewAnswer(e.target.value)}
              placeholder="Enter the answer to provide..."
              sx={{ mb: 2 }}
              helperText={`${newAnswer.length} characters`}
            />
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Topic</InputLabel>
              <Select
                value={newTopic}
                label="Topic"
                onChange={(e) => setNewTopic(e.target.value)}
              >
                {TOPIC_OPTIONS.map(topic => (
                  <MenuItem key={topic} value={topic}>{topic}</MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {/* Scope Selection */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>FAQ Scope</InputLabel>
              <Select
                value={newScope}
                label="FAQ Scope"
                onChange={(e) => {
                  setNewScope(e.target.value);
                  if (e.target.value === 'company') {
                    setNewTeamId('');
                  }
                }}
              >
                <MenuItem value="company">Company-Wide (All Teams)</MenuItem>
                <MenuItem value="team">Team-Specific</MenuItem>
              </Select>
            </FormControl>
            
            {/* Team Selection (shown only if team scope selected) */}
            {newScope === 'team' && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Select Team</InputLabel>
                <Select
                  value={newTeamId}
                  label="Select Team"
                  onChange={(e) => setNewTeamId(e.target.value)}
                >
                  {userTeams.length === 0 && (
                    <MenuItem disabled value="">No teams available</MenuItem>
                  )}
                  {userTeams.map(team => (
                    <MenuItem key={team.id} value={team.id}>{team.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                startIcon={<CancelIcon />}
                onClick={() => {
                  setShowCreateForm(false);
                  setNewQuestion('');
                  setNewAnswer('');
                  setNewTopic('General');
                  setNewScope('company');
                  setNewTeamId('');
                }}
                disabled={isCreating}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={isCreating ? <CircularProgress size={20} /> : <SaveIcon />}
                onClick={handleCreateFAQ}
                disabled={isCreating || !newQuestion.trim() || !newAnswer.trim()}
              >
                {isCreating ? 'Creating...' : 'Save FAQ'}
              </Button>
            </Box>
          </Paper>
        </Collapse>

        {/* Published FAQs Tab */}
        {activeTab === 'published' && (
          <>
            {/* Search and Filter */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                  placeholder="Search FAQs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  size="small"
                  sx={{ flexGrow: 1, minWidth: '200px' }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    )
                  }}
                />
            
            {/* Team Filter */}
            {userTeams.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Team</InputLabel>
                <Select
                  value={selectedTeam}
                  label="Team"
                  onChange={(e) => setSelectedTeam(e.target.value)}
                >
                  <MenuItem value="all">All Teams</MenuItem>
                  {userTeams.map(team => (
                    <MenuItem key={team.id} value={team.id}>{team.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Topic</InputLabel>
              <Select
                value={filterTopic}
                label="Topic"
                onChange={(e) => setFilterTopic(e.target.value)}
              >
                <MenuItem value="all">All Topics</MenuItem>
                {availableTopics.map(topic => (
                  <MenuItem key={topic} value={topic}>{topic}</MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                label="Sort By"
                onChange={(e) => setSortBy(e.target.value)}
              >
                <MenuItem value="usage">Most Used</MenuItem>
                <MenuItem value="date">Newest First</MenuItem>
                <MenuItem value="quality">Highest Quality</MenuItem>
              </Select>
            </FormControl>
            
            <Typography variant="body2" color="text.secondary">
              {filteredAndSortedFAQs.length} FAQ{filteredAndSortedFAQs.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </Paper>

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* FAQ List */}
        {!loading && filteredAndSortedFAQs.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <QuestionAnswerIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No FAQs found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {searchQuery || filterTopic !== 'all' 
                ? 'Try adjusting your search or filters'
                : 'Create your first FAQ to get started'}
            </Typography>
            {!searchQuery && filterTopic === 'all' && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowCreateForm(true)}
              >
                Create New FAQ
              </Button>
            )}
          </Paper>
        )}

        {!loading && filteredAndSortedFAQs.map((faq, index) => (
          <Card key={faq.id} sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                  {index + 1}. {editingFaqId === faq.id ? 'Editing FAQ' : faq.topic || 'General'}
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {faq.usage_count > 0 && (
                    <Chip
                      icon={<StarIcon />}
                      label={`Used ${faq.usage_count}x`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                  {faq.quality_score && (
                    <Chip
                      label={`Quality: ${(faq.quality_score * 100).toFixed(0)}%`}
                      size="small"
                      color={faq.quality_score >= 0.8 ? 'success' : 'default'}
                      variant="outlined"
                    />
                  )}
                </Box>
              </Box>
              
              <Divider sx={{ mb: 2 }} />
              
              {editingFaqId === faq.id ? (
                // Edit Mode
                <>
                  <TextField
                    label="Question"
                    fullWidth
                    multiline
                    rows={2}
                    value={editQuestion}
                    onChange={(e) => setEditQuestion(e.target.value)}
                    sx={{ mb: 2 }}
                    helperText={`${editQuestion.length} characters`}
                  />
                  
                  <TextField
                    label="Answer"
                    fullWidth
                    multiline
                    rows={4}
                    value={editAnswer}
                    onChange={(e) => setEditAnswer(e.target.value)}
                    sx={{ mb: 2 }}
                    helperText={`${editAnswer.length} characters`}
                  />
                  
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Topic</InputLabel>
                    <Select
                      value={editTopic}
                      label="Topic"
                      onChange={(e) => setEditTopic(e.target.value)}
                    >
                      {TOPIC_OPTIONS.map(topic => (
                        <MenuItem key={topic} value={topic}>{topic}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </>
              ) : (
                // View Mode
                <>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Q: {faq.question}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    ({faq.question.length} characters)
                  </Typography>
                  
                  <Typography variant="body1" sx={{ mt: 2, mb: 1 }}>
                    A: {faq.answer}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ({faq.answer.length} characters)
                  </Typography>
                  
                  {faq.created_at && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                      Created: {new Date(faq.created_at).toLocaleString()}
                    </Typography>
                  )}
                </>
              )}
            </CardContent>
            
            <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
              {editingFaqId === faq.id ? (
                <>
                  <Button
                    startIcon={<CancelIcon />}
                    onClick={handleCancelEdit}
                    disabled={isEditing}
                    size="small"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={isEditing ? <CircularProgress size={20} /> : <SaveIcon />}
                    onClick={handleSaveEdit}
                    disabled={isEditing || !editQuestion.trim() || !editAnswer.trim()}
                    size="small"
                  >
                    {isEditing ? 'Saving...' : 'Save Changes'}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    startIcon={<EditIcon />}
                    onClick={() => handleStartEdit(faq)}
                    size="small"
                  >
                    Edit
                  </Button>
                  <Button
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDeleteClick(faq)}
                    color="error"
                    size="small"
                  >
                    Delete
                  </Button>
                </>
              )}
            </CardActions>
          </Card>
        ))}
          </>
        )}

        {/* Pending FAQs Tab */}
        {activeTab === 'pending' && (
          <>
            {/* Loading State */}
            {loadingPending && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            )}

            {/* Empty State */}
            {!loadingPending && pendingFaqs.length === 0 && (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <QuestionAnswerIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No pending FAQ suggestions
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  When employees edit AI answers before sending, they'll appear here for your review.
                </Typography>
              </Paper>
            )}

            {/* Pending FAQ Cards */}
            {!loadingPending && pendingFaqs.map((suggestion, index) => (
              <Card key={suggestion.id} sx={{ mb: 2, border: '2px solid #ff9800' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Chip label="Pending Review" color="warning" size="small" sx={{ mb: 1 }} />
                      <Typography variant="caption" display="block" color="text.secondary">
                        Suggested from conversation #{suggestion.conversation_id}
                      </Typography>
                    </Box>
                    <Chip 
                      label={`Quality: ${(suggestion.quality_score * 100).toFixed(0)}%`} 
                      size="small" 
                      color={suggestion.quality_score >= 0.8 ? 'success' : 'default'}
                    />
                  </Box>

                  {/* Question */}
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: '#1976d2' }}>
                    Question:
                  </Typography>
                  {reviewingSuggestionId === suggestion.id ? (
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      value={reviewQuestion}
                      onChange={(e) => setReviewQuestion(e.target.value)}
                      sx={{ mb: 2 }}
                    />
                  ) : (
                    <Typography variant="body1" paragraph sx={{ pl: 2, bgcolor: '#f5f5f5', p: 1.5, borderRadius: 1 }}>
                      {suggestion.question}
                    </Typography>
                  )}

                  {/* Answer */}
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: '#1976d2' }}>
                    {suggestion.edited_answer ? 'Edited Answer (Employee Modified):' : 'Original Answer:'}
                  </Typography>
                  {reviewingSuggestionId === suggestion.id ? (
                    <TextField
                      fullWidth
                      multiline
                      rows={6}
                      value={reviewAnswer}
                      onChange={(e) => setReviewAnswer(e.target.value)}
                      sx={{ mb: 2 }}
                    />
                  ) : (
                    <Typography variant="body2" paragraph sx={{ pl: 2, bgcolor: suggestion.edited_answer ? '#fff3e0' : '#f5f5f5', p: 1.5, borderRadius: 1 }}>
                      {suggestion.edited_answer || suggestion.original_answer}
                    </Typography>
                  )}

                  {/* Show original if edited */}
                  {suggestion.edited_answer && reviewingSuggestionId !== suggestion.id && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                        Original AI Answer (for comparison):
                      </Typography>
                      <Typography variant="body2" sx={{ pl: 2, bgcolor: '#f5f5f5', p: 1.5, borderRadius: 1, fontStyle: 'italic' }}>
                        {suggestion.original_answer}
                      </Typography>
                    </>
                  )}

                  {/* Topic Selection (when reviewing) */}
                  {reviewingSuggestionId === suggestion.id && (
                    <FormControl fullWidth sx={{ mt: 2 }}>
                      <InputLabel>Topic</InputLabel>
                      <Select
                        value={reviewTopic}
                        label="Topic"
                        onChange={(e) => setReviewTopic(e.target.value)}
                      >
                        {TOPIC_OPTIONS.map(topic => (
                          <MenuItem key={topic} value={topic}>{topic}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </CardContent>

                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                  {reviewingSuggestionId === suggestion.id ? (
                    <>
                      <Button
                        startIcon={<CancelIcon />}
                        onClick={handleCancelReview}
                        disabled={isApproving}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={isApproving ? <CircularProgress size={20} /> : <SaveIcon />}
                        onClick={handleApprovePending}
                        disabled={isApproving}
                      >
                        {isApproving ? 'Approving...' : 'Approve & Create FAQ'}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        startIcon={<DeleteIcon />}
                        color="error"
                        onClick={() => handleRejectPending(suggestion.id)}
                      >
                        Reject
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<EditIcon />}
                        onClick={() => handleStartReview(suggestion)}
                      >
                        Review & Edit
                      </Button>
                    </>
                  )}
                </CardActions>
              </Card>
            ))}
          </>
        )}
      </Container>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => !isDeleting && setDeleteDialogOpen(false)}>
        <DialogTitle>Delete FAQ?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this FAQ?
          </Typography>
          {faqToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                <strong>Q:</strong> {faqToDelete.question}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>A:</strong> {faqToDelete.answer.substring(0, 100)}
                {faqToDelete.answer.length > 100 ? '...' : ''}
              </Typography>
            </Box>
          )}
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action cannot be undone. The FAQ will be removed from the database and search index.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {isDeleting ? 'Deleting...' : 'Delete FAQ'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default FAQManagement;

